import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next/';
import Image from 'next/image';
import { GetStaticPropsContext } from 'next';

import styles from './Single.module.scss';
import Content from '@utils/Content';

import NewsList from '@components/lists/NewsList/NewsList';
import NewsListItemProps from '@components/ui/NewsListItem/NewsListItem.props';
import { useAddView } from '@hooks/useAddView';
import PreprUtil from '@utils/PreprUtil';
import Sidebar from '@components/containers/Sidebar/Sidebar';
import TipCTA from '@components/containers/TipCTA/TipCTA';
import { Meta } from '@components/layout/Meta/Meta';
import Author from '@components/ui/Author/Author';
import CookieDialog from '@components/forms/CookieDialog/CookieDialog';
import Layout from '@components/layout/Layout/Layout';

import {
  DestinationLinksProvider,
  FooterLinksProvider,
  SiteLinksProvider,
  useAdvertisement,
  useLocale,
} from '@hooks/index';

import {
  ADVERTISEMENT_DEVICE,
  ADVERTISEMENT_TYPE,
  FB_APP_ID,
  NavbarTabsTypes,
  PageProps,
  REVALIDATION_TIME,
} from '@constants/index';
import GraphqlService from '@services/GraphqlService';
import PersonalService from '@services/PersonalService';
import GenericService from '@services/GenericService';
import MailchimpService from '@services/MailchimpService';
import { INewsArticlePublicationFields, NewsModule } from 'modules/NewsModule';
import { getRoute } from '@constants/routes';
import { Advertisement } from '@components/layout/Advertisement/Advertisement';
import { ITextElement } from '@models/content_elements/Text/types';
import StringsUtil from '@utils/StringsUtil';

const { FacebookProvider } = require('react-facebook');

interface Props extends PageProps<INewsArticlePublicationFields> {}

const NewsArticlePage = ({ fields, siteLinks, footerLinks, destinationLinks }: Props) => {
  useAdvertisement(
    ADVERTISEMENT_TYPE.ARTICLE,
    ADVERTISEMENT_DEVICE.DESKTOP_AND_MOBILE,
    `.${styles.NewsArticlePage} h2`,
    1,
  );
  const { t } = useTranslation();
  const locale = useLocale();

  useAddView(fields._id);

  const author = fields.author?.[0];
  const sidebar = fields.sidebar;
  const travelTips = fields.travel_tip?.[0];

  const getRelated = useCallback(() => {
    if (!fields.related) return [];

    return fields.related.map<NewsListItemProps>((item) =>
      PreprUtil.formatArticle(item, getRoute(locale as string, 'NEWS')),
    );
  }, [fields.related]);

  const renderContent = () => {
    return fields.content.map((item, index) => {
      return (
        <div key={index} className={styles[item.__typename]}>
          {Content.renderPreprElement(item)}
        </div>
      );
    });
  };

  const firstTextField = fields.content.find((element) => {
    return element.__typename === 'Text';
  }) as ITextElement;

  return (
    <FacebookProvider appId={FB_APP_ID} xfbml>
      <FooterLinksProvider value={footerLinks}>
        <DestinationLinksProvider value={destinationLinks}>
          <SiteLinksProvider value={siteLinks}>
            <Layout selectedTab={NavbarTabsTypes.NEWS} paddings={false}>
              <div className={styles.NewsArticlePage}>
                <Meta
                  title={fields.meta_title || fields.title}
                  description={
                    fields.meta_description ||
                    fields.excerpt ||
                    StringsUtil.stripHTML(firstTextField.body)
                  }
                  image={fields.thumbnail?.[0]?.cdn_files[0]?.full}
                />
                <div className="container">
                  <div className="row">
                    <div className="col-lg-9">
                      <div className={styles.Main}>
                        <div>
                          <Advertisement
                            advertisementType={ADVERTISEMENT_TYPE.ARTICLE}
                            advertisementDevice={ADVERTISEMENT_DEVICE.MOBILE}
                          />
                        </div>
                        <h1>{fields.title}</h1>
                        <div className={styles.Thumbnail}>
                          <Image
                            src={fields.thumbnail[0].cdn_files[0]?.full}
                            alt=""
                            quality={60}
                            layout="fill"
                          />
                          {fields.thumbnail[0].author && (
                            <div
                              className={styles.Copyright}>{`©${fields.thumbnail[0].author}`}</div>
                          )}
                        </div>
                        {!!author && (
                          <div className={styles.Author}>
                            <Author
                              url={`/${siteLinks?.about_corno?.[0]._slug}`}
                              author={author.name}
                              image={author.image[0].cdn_files[0]?.full}
                            />
                          </div>
                        )}

                        <div className={styles.Excerpt}>{fields.excerpt}</div>
                        <div className={styles.Content}>{!!fields.content && renderContent()}</div>
                      </div>
                    </div>
                    {!!sidebar && (
                      <div className="col-lg-3 mt-md">
                        <Advertisement
                          className="mt-0 mb-md"
                          advertisementType={ADVERTISEMENT_TYPE.SIDEBAR}
                          advertisementDevice={ADVERTISEMENT_DEVICE.DESKTOP_AND_MOBILE}
                        />
                        <Sidebar sidebar={sidebar} />
                      </div>
                    )}
                  </div>
                  <div className="row">
                    <div className="col-md-12">
                      <div className={styles.Related}>
                        <NewsList
                          title={t('GENERIC.READ_ALSO')}
                          articles={getRelated()}
                          icon={require('@assets/images/icons/thumbs-up.svg')}
                        />
                      </div>

                      {travelTips && (
                        <div className={styles.TravelTips}>
                          <TipCTA
                            title={travelTips.display_title}
                            subTitle={travelTips.subtitle}
                            backgroundImage={travelTips.background_image[0].cdn_files[0]?.full}
                            link={{
                              url: travelTips.button_url,
                              label: travelTips.button_label,
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Layout>
            <CookieDialog />
          </SiteLinksProvider>
        </DestinationLinksProvider>
      </FooterLinksProvider>
    </FacebookProvider>
  );
};

export default NewsArticlePage;

export const getStaticProps = async ({ params, locale, preview }: GetStaticPropsContext) => {
  if (!params) {
    return {
      notFound: true,
    };
  }

  GraphqlService.setLocale(locale);
  PersonalService.setLocale(locale);
  GenericService.setLocale(locale);
  MailchimpService.setLocale(locale);

  GraphqlService.setPreview(!!preview);

  const page = await NewsModule.getSinglePage(params.news as string);
  if (!page || !page.fields) {
    return {
      notFound: true,
      revalidate: REVALIDATION_TIME,
    };
  }

  return {
    props: page,
    revalidate: REVALIDATION_TIME,
  };
};

export const getStaticPaths = async () => {
  const paths = {
    paths: [],
    fallback: 'blocking',
  };

  return paths;
};
